import {
  memo,
  forwardRef,
  useCallback,
  ForwardedRef,
  useImperativeHandle,
} from 'react';
import {
  withDelay,
  withTiming,
  useSharedValue,
  useAnimatedStyle,
  runOnJS,
} from 'react-native-reanimated';
import { StackNavigationProp, useNavigation } from '@betty/navigation';
import { Box, Text, Button } from '@betty/moonlight';
import {
  GamesRandomizerSelectionProps,
  GamesRandomizerSelectionExposedProps,
} from './GamesRandomizerSelection.types';
import Thumbnail from '../../Games/Game/Thumbnail/Thumbnail';

function GamesRandomizerSelection(
  { targetGameId, onButtonPress }: GamesRandomizerSelectionProps,
  ref: ForwardedRef<GamesRandomizerSelectionExposedProps>,
) {
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const animatedValue = useSharedValue(0);
  const animatedStyle = useAnimatedStyle(
    () => ({
      opacity: animatedValue.value,
      pointerEvents: animatedValue.value ? 'auto' : 'none',
    }),
    [animatedValue],
  );
  const animatedStyleThumbnail = useAnimatedStyle(
    () => ({
      transform: [{ scale: Number(animatedValue.value) }],
    }),
    [animatedValue],
  );

  const show = useCallback(
    (onComplete: () => void) => {
      animatedValue.value = withDelay(
        2000,
        withTiming(1, undefined, () => {
          runOnJS(onComplete)();
        }),
      );
    },
    [animatedValue],
  );

  const hide = useCallback(
    (onComplete: () => void) => {
      animatedValue.value = withTiming(0, undefined, () => {
        runOnJS(onComplete)();
      });
    },
    [animatedValue],
  );

  const onPlayNow = useCallback(() => {
    if (targetGameId) {
      navigation.navigate('GameplayScreen', { id: targetGameId });

      onButtonPress();
    }
  }, [targetGameId, navigation, onButtonPress]);

  useImperativeHandle(
    ref,
    () => ({
      show,
      hide,
    }),
    [show, hide],
  );

  return targetGameId === undefined ? null : (
    <Box
      variant="gradient"
      borderRadius="radius-400"
      paddingHorizontal="space-800"
      justifyContent="center"
      gap="space-500"
      animatedStyle={animatedStyle}
      absoluteFill
    >
      <Text
        variant="h5"
        locNS="games"
        locKey="randomizerPlayTitle"
        color="accentSurfacePrimary"
        textAlign="center"
      />
      <Box
        width={195}
        height={195}
        alignSelf="center"
        borderRadius="radius-400"
        animatedStyle={animatedStyleThumbnail}
        overflow="hidden"
      >
        <Thumbnail
          gameId={targetGameId}
          type="square"
          width="100%"
          height="100%"
        />
      </Box>
      <Box width="70%" maxWidth={330} alignSelf="center" gap="space-200">
        <Button
          locNS="games"
          locKey="randomizerPlayNow"
          onPress={onPlayNow}
          light
        />
        <Button
          locNS="games"
          locKey="randomizerRespin"
          variant="secondary"
          onPress={onButtonPress}
          light
        />
      </Box>
    </Box>
  );
}

export default memo(forwardRef(GamesRandomizerSelection));
